import { App } from 'vue';

const CHUNK_LOAD_ERROR_RELOAD_KEY = 'chunk-load-error-reloaded';
const CHUNK_LOAD_ERROR_HARD_RELOAD_KEY = 'chunk-load-error-hard-reloaded';

let isChunkLoadRecoveryInitialized = false;

function isChunkLoadError(error: unknown): boolean {
   const message = error instanceof Error ? error.message : String(error ?? '');

   return (
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('error loading dynamically imported module') ||
      message.includes('Importing a module script failed') ||
      message.includes('Unable to preload CSS')
   );
}

async function hardRefreshAfterChunkError() {
   try {
      if ('serviceWorker' in navigator) {
         const registrations = await navigator.serviceWorker.getRegistrations();
         await Promise.all(registrations.map((registration) => registration.unregister()));
      }

      if ('caches' in window) {
         const cacheKeys = await caches.keys();
         await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
      }
   } catch (cleanupError) {
      console.warn('Failed to clear service worker state during chunk load recovery:', cleanupError);
   }

   const hardRefreshUrl = new URL(window.location.href);
   hardRefreshUrl.searchParams.set('_reloaded', Date.now().toString());
   window.location.replace(hardRefreshUrl.toString());
}

function handleChunkLoadError(error: unknown): boolean {
   if (!isChunkLoadError(error)) {
      return false;
   }

   const hasReloaded = sessionStorage.getItem(CHUNK_LOAD_ERROR_RELOAD_KEY);
   const hasHardReloaded = sessionStorage.getItem(CHUNK_LOAD_ERROR_HARD_RELOAD_KEY);

   if (!hasReloaded) {
      sessionStorage.setItem(CHUNK_LOAD_ERROR_RELOAD_KEY, 'true');
      window.location.reload();
      return true;
   }

   if (!hasHardReloaded) {
      sessionStorage.setItem(CHUNK_LOAD_ERROR_HARD_RELOAD_KEY, 'true');
      void hardRefreshAfterChunkError();
      return true;
   }

   sessionStorage.removeItem(CHUNK_LOAD_ERROR_RELOAD_KEY);
   sessionStorage.removeItem(CHUNK_LOAD_ERROR_HARD_RELOAD_KEY);
   console.error('Chunk loading failed after hard refresh recovery attempts:', error);

   return true;
}

export function initChunkLoadRecovery(app: App) {
   if (isChunkLoadRecoveryInitialized) {
      return;
   }

   isChunkLoadRecoveryInitialized = true;
   const previousErrorHandler = app.config.errorHandler;

   window.addEventListener('load', () => {
      sessionStorage.removeItem(CHUNK_LOAD_ERROR_RELOAD_KEY);
      sessionStorage.removeItem(CHUNK_LOAD_ERROR_HARD_RELOAD_KEY);
   });

   window.addEventListener('unhandledrejection', (event) => {
      if (handleChunkLoadError(event.reason)) {
         event.preventDefault();
      }
   });

   window.addEventListener('error', (event) => {
      if (handleChunkLoadError(event.error ?? event.message)) {
         event.preventDefault();
      }
   });

   app.config.errorHandler = (err, instance, info) => {
      if (handleChunkLoadError(err)) {
         return;
      }

      // A throwing error handler aborts Vue's job flush, leaving the UI in a
      // half-rendered state until a full reload. Never let a downstream handler
      // propagate out of here.
      try {
         if (previousErrorHandler) {
            previousErrorHandler(err, instance, info);
            return;
         }

         console.error(`Error in ${info}:`, err);
      } catch (handlerError) {
         console.error('Vue error handler threw while handling an error:', handlerError);
      }
   };
}
